import { createContext, useContext, useState, useMemo, useEffect, useCallback, type ReactNode } from 'react';
import { useAuth } from './AuthContext';
import { updateUserPreferences } from '../config/data/firebase';
import { collections } from '../config/data/collections';

export interface SidebarContextValue {
	collapsed: boolean;
	setCollapsed: (newState?: boolean) => void;
}

const SidebarContext = createContext<SidebarContextValue | undefined>(undefined);

export const SidebarProvider = ({ children }: { children: ReactNode }) => {
	const [collapsed, setCollapsed] = useState<boolean>(() => {
		const savedState = localStorage.getItem('pf_sidebar_collapsed');
		return savedState === 'true';
	});

	const { member: rawMember, user } = useAuth();
	const member = rawMember as Record<string, Record<string, unknown>> | null;

	useEffect(() => {
		if (member?.preferences?.sidebarCollapsed !== undefined) {
			setCollapsed(Boolean(member.preferences.sidebarCollapsed));
			localStorage.setItem('pf_sidebar_collapsed', String(member.preferences.sidebarCollapsed));
		}
	}, [member]);

	const toggleSidebar = useCallback(
		(newState?: boolean) => {
			const valueToSet = typeof newState === 'boolean' ? newState : !collapsed;

			setCollapsed(valueToSet);

			localStorage.setItem('pf_sidebar_collapsed', String(valueToSet));

			if (member && user) {
				updateUserPreferences(user.uid, collections.members, { sidebarCollapsed: valueToSet });
			}
		},
		[collapsed, user, member]
	);

	const value = useMemo<SidebarContextValue>(
		() => ({
			collapsed,
			setCollapsed: toggleSidebar,
		}),
		[collapsed, toggleSidebar]
	);

	return <SidebarContext.Provider value={value}>{children}</SidebarContext.Provider>;
};

export const useSidebar = (): SidebarContextValue => {
	const context = useContext(SidebarContext);
	if (context === undefined) {
		throw new Error('useSidebar must be used within a SidebarProvider');
	}
	return context;
};
